import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Building2, Copy, RefreshCw, 
  CheckCircle2, XCircle, Eye, Lock, Unlock, Users,
  MessageSquare, Phone, Mail, MapPin, DollarSign
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const statusColors = {
  active: 'bg-green-100 text-green-700',
  suspended: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-700'
};

const statusLabels = {
  active: 'Ativa',
  suspended: 'Suspensa',
  cancelled: 'Cancelada'
};

const planLabels = {
  basic: 'Básico',
  professional: 'Profissional',
  enterprise: 'Empresarial'
};

function TenantsManagement() {
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    cnpj: '',
    phone: '',
    address: '',
    responsible_name: '',
    responsible_email: '',
    plan_type: 'basic',
    plan_value: 0,
    max_users: 10,
    max_sectors: 5,
    whatsapp_numbers_limit: 3
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [tenantsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/tenants`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/tenants/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTenants(tenantsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
      alert('Erro ao carregar dados');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (editing) {
        await axios.put(`${API}/api/tenants/${editing.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/api/tenants`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setShowModal(false);
      setEditing(null);
      resetForm();
      loadData();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '', cnpj: '', phone: '', address: '',
      responsible_name: '', responsible_email: '',
      plan_type: 'basic', plan_value: 0,
      max_users: 10, max_sectors: 5, whatsapp_numbers_limit: 3
    });
  };

  const handleEdit = (tenant) => {
    setEditing(tenant);
    setForm({
      name: tenant.name,
      cnpj: tenant.cnpj || '',
      phone: tenant.phone || '',
      address: tenant.address || '',
      responsible_name: tenant.responsible_name || '',
      responsible_email: tenant.responsible_email || '',
      plan_type: tenant.plan_type,
      plan_value: tenant.plan_value,
      max_users: tenant.max_users,
      max_sectors: tenant.max_sectors,
      whatsapp_numbers_limit: tenant.whatsapp_numbers_limit
    });
    setShowModal(true);
  };

  const handleSuspend = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/tenants/${selectedTenant.id}/suspend`, 
        { reason: suspendReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowSuspendModal(false);
      setSuspendReason('');
      setSelectedTenant(null);
      loadData();
    } catch (err) {
      alert('Erro ao suspender: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleActivate = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/tenants/${id}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadData();
    } catch (err) {
      alert('Erro ao ativar: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta empresa? Esta ação não pode ser desfeita!')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/api/tenants/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        loadData();
      } catch (err) {
        alert('Erro ao excluir: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const handleRegenerateCode = async (id) => {
    if (window.confirm('Regenerar o código de convite? O código anterior deixará de funcionar.')) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/api/tenants/${id}/regenerate-code`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        loadData();
      } catch (err) {
        alert('Erro ao regenerar código: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const copyInvitationCode = (code) => {
    navigator.clipboard.writeText(code);
    alert('Código copiado: ' + code);
  };

  const formatCNPJ = (cnpj) => {
    if (!cnpj) return '—';
    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciamento de Empresas</h1>
          <p className="text-gray-500">Cadastre e gerencie as empresas que acessam o sistema</p>
        </div>
        <button 
          onClick={() => { setEditing(null); resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 shadow-lg shadow-green-100">
          <Plus size={18} /> Nova Empresa
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
              <Building2 size={20} />
            </div>
            <span className="text-sm text-gray-500">Total de Empresas</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{tenants.length}</p>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-50 text-green-600 rounded-xl">
              <CheckCircle2 size={20} />
            </div>
            <span className="text-sm text-gray-500">Empresas Ativas</span>
          </div>
          <p className="text-3xl font-bold text-green-600">
            {tenants.filter(t => t.status === 'active').length}
          </p>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-red-50 text-red-600 rounded-xl">
              <XCircle size={20} />
            </div>
            <span className="text-sm text-gray-500">Empresas Suspensas</span>
          </div>
          <p className="text-3xl font-bold text-red-600">
            {tenants.filter(t => t.status === 'suspended').length}
          </p>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-50 text-purple-600 rounded-xl">
              <DollarSign size={20} />
            </div>
            <span className="text-sm text-gray-500">Receita Mensal</span>
          </div>
          <p className="text-3xl font-bold text-purple-600">
            {formatCurrency(tenants.reduce((sum, t) => sum + (t.plan_value || 0), 0))}
          </p>
        </div>
      </div>

      {/* Lista de Empresas */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-6 py-4 font-semibold">Empresa</th>
                <th className="px-6 py-4 font-semibold">Código de Acesso</th>
                <th className="px-6 py-4 font-semibold">Plano</th>
                <th className="px-6 py-4 font-semibold">Status</th>
                <th className="px-6 py-4 font-semibold">Usuários</th>
                <th className="px-6 py-4 font-semibold">Conversas</th>
                <th className="px-6 py-4 font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {stats.map(stat => {
                const tenant = tenants.find(t => t.id === stat.id);
                if (!tenant) return null;

                return (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold">
                          {tenant.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{tenant.name}</p>
                          <p className="text-xs text-gray-500">{formatCNPJ(tenant.cnpj)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <code className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">
                          {tenant.invitation_code}
                        </code>
                        <button 
                          onClick={() => copyInvitationCode(tenant.invitation_code)}
                          className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                          <Copy size={14} />
                        </button>
                        <button 
                          onClick={() => handleRegenerateCode(tenant.id)}
                          className="p-1 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded"
                          title="Regenerar código">
                          <RefreshCw size={14} />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{planLabels[tenant.plan_type]}</p>
                        <p className="text-xs text-gray-500">{formatCurrency(tenant.plan_value)}/mês</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${statusColors[tenant.status]}`}>
                        {statusLabels[tenant.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Users size={14} />
                        {stat.users_count} / {tenant.max_users}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <MessageSquare size={14} />
                        {stat.conversations_count}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1">
                        <button 
                          onClick={() => { setSelectedTenant(tenant); setShowDetailsModal(true); }}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Ver detalhes">
                          <Eye size={16} />
                        </button>
                        <button 
                          onClick={() => handleEdit(tenant)}
                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                          title="Editar">
                          <Edit2 size={16} />
                        </button>
                        {tenant.status === 'active' ? (
                          <button 
                            onClick={() => { setSelectedTenant(tenant); setShowSuspendModal(true); }}
                            className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg"
                            title="Suspender">
                            <Lock size={16} />
                          </button>
                        ) : (
                          <button 
                            onClick={() => handleActivate(tenant.id)}
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                            title="Ativar">
                            <Unlock size={16} />
                          </button>
                        )}
                        <button 
                          onClick={() => handleDelete(tenant.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title="Excluir">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {stats.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-400">
                    Nenhuma empresa cadastrada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold mb-4">{editing ? 'Editar Empresa' : 'Nova Empresa'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Empresa *</label>
                  <input 
                    type="text" 
                    value={form.name} 
                    onChange={e => setForm({...form, name: e.target.value})} 
                    required
                    placeholder="Ex: Cartão da Família"
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CNPJ</label>
                  <input 
                    type="text" 
                    value={form.cnpj} 
                    onChange={e => setForm({...form, cnpj: e.target.value})} 
                    placeholder="00.000.000/0000-00"
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <input 
                    type="tel" 
                    value={form.phone} 
                    onChange={e => setForm({...form, phone: e.target.value})} 
                    placeholder="(00) 00000-0000"
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Responsável</label>
                  <input 
                    type="text" 
                    value={form.responsible_name} 
                    onChange={e => setForm({...form, responsible_name: e.target.value})} 
                    placeholder="Nome do responsável"
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email do Responsável</label>
                  <input 
                    type="email" 
                    value={form.responsible_email} 
                    onChange={e => setForm({...form, responsible_email: e.target.value})} 
                    placeholder="responsavel@empresa.com"
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plano</label>
                  <select 
                    value={form.plan_type} 
                    onChange={e => setForm({...form, plan_type: e.target.value})}
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
                  >
                    <option value="basic">Básico</option>
                    <option value="professional">Profissional</option>
                    <option value="enterprise">Empresarial</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor Mensal (R$)</label>
                  <input 
                    type="number" 
                    step="0.01"
                    value={form.plan_value} 
                    onChange={e => setForm({...form, plan_value: parseFloat(e.target.value) || 0})} 
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Máx. Usuários</label>
                  <input 
                    type="number" 
                    value={form.max_users} 
                    onChange={e => setForm({...form, max_users: parseInt(e.target.value) || 10})} 
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Máx. Setores</label>
                  <input 
                    type="number" 
                    value={form.max_sectors} 
                    onChange={e => setForm({...form, max_sectors: parseInt(e.target.value) || 5})} 
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Máx. Números WhatsApp</label>
                  <input 
                    type="number" 
                    value={form.whatsapp_numbers_limit} 
                    onChange={e => setForm({...form, whatsapp_numbers_limit: parseInt(e.target.value) || 3})} 
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
                <textarea 
                  value={form.address} 
                  onChange={e => setForm({...form, address: e.target.value})} 
                  rows={2}
                  placeholder="Endereço completo"
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none resize-none" 
                />
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  disabled={loading}
                  className="px-5 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 disabled:opacity-50">
                  {loading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Detalhes */}
      {showDetailsModal && selectedTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Detalhes da Empresa</h2>
              <button onClick={() => setShowDetailsModal(false)} className="text-gray-400 hover:text-gray-600">
                <XCircle size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Nome</p>
                  <p className="font-medium">{selectedTenant.name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">CNPJ</p>
                  <p className="font-medium">{formatCNPJ(selectedTenant.cnpj)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Telefone</p>
                  <p className="font-medium flex items-center gap-1">
                    <Phone size={14} /> {selectedTenant.phone || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email Responsável</p>
                  <p className="font-medium flex items-center gap-1">
                    <Mail size={14} /> {selectedTenant.responsible_email || '—'}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">Endereço</p>
                  <p className="font-medium flex items-center gap-1">
                    <MapPin size={14} /> {selectedTenant.address || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Código de Acesso</p>
                  <div className="flex items-center gap-2">
                    <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">
                      {selectedTenant.invitation_code}
                    </code>
                    <button 
                      onClick={() => copyInvitationCode(selectedTenant.invitation_code)}
                      className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                      <Copy size={14} />
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Plano</p>
                  <p className="font-medium">{planLabels[selectedTenant.plan_type]} - {formatCurrency(selectedTenant.plan_value)}/mês</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${statusColors[selectedTenant.status]}`}>
                    {statusLabels[selectedTenant.status]}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Criada em</p>
                  <p className="font-medium">{new Date(selectedTenant.created_at).toLocaleDateString('pt-BR')}</p>
                </div>
              </div>
              {selectedTenant.status === 'suspended' && selectedTenant.suspended_reason && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-xs text-red-700 font-medium">Motivo da Suspensão:</p>
                  <p className="text-sm text-red-900">{selectedTenant.suspended_reason}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Suspensão */}
      {showSuspendModal && selectedTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
            <h2 className="text-lg font-bold mb-4">Suspender Empresa</h2>
            <p className="text-sm text-gray-600 mb-4">
              Ao suspender <strong>{selectedTenant.name}</strong>, todos os usuários perderão acesso ao sistema.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo da Suspensão</label>
                <textarea 
                  value={suspendReason} 
                  onChange={e => setSuspendReason(e.target.value)} 
                  rows={3}
                  placeholder="Ex: Inadimplência, Violação dos termos de uso..."
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-500 outline-none resize-none" 
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button 
                  type="button" 
                  onClick={() => { setShowSuspendModal(false); setSuspendReason(''); }}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">
                  Cancelar
                </button>
                <button 
                  onClick={handleSuspend}
                  className="px-5 py-2 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700">
                  Suspender
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TenantsManagement;